import { useMemo, useCallback } from 'react';
import { Country, State, City } from 'country-state-city';
import { MapPin } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';

/**
 * Cascading Country → State → City picker (backed by country-state-city).
 * Persists names (not ISO codes) via `hq_city`, `hq_state`, `hq_country` — same
 * shape the venture editors + Lusha fetch already use, so nothing downstream
 * needs to change.
 *
 * Props:
 *   value    = { hq_city, hq_state, hq_country }
 *   onChange = (patch) => void  — receives ONLY the changed keys
 *   required = boolean          — renders the * marker + red border (Country)
 *   optionalStateCity = boolean — when true, State/City are OPTIONAL: no *
 *                                 marker, placeholders read "Leave blank for
 *                                 all states / all cities". Used by Discover
 *                                 admin where nation-wide opportunities are
 *                                 common (Startup India, RBI, etc.).
 *   testidPrefix = string       — data-testids: {prefix}-country/state/city
 */
export const LocationPicker = ({ value = {}, onChange, required = false, optionalStateCity = false, testidPrefix = 'location' }) => {
  const countries = useMemo(() => Country.getAllCountries(), []);
  const country = useMemo(() =>
    countries.find((c) => c.name === value.hq_country) || null,
  [countries, value.hq_country]);

  const states = useMemo(() =>
    country ? State.getStatesOfCountry(country.isoCode) : [],
  [country]);
  const state = useMemo(() =>
    states.find((s) => s.name === value.hq_state) || null,
  [states, value.hq_state]);

  const cities = useMemo(() =>
    country && state ? City.getCitiesOfState(country.isoCode, state.isoCode) : [],
  [country, state]);

  const countryOpts = useMemo(() =>
    countries.map((c) => ({ value: c.isoCode, label: `${c.flag} ${c.name}` })),
  [countries]);
  const stateOpts = useMemo(() =>
    states.map((s) => ({ value: s.isoCode, label: s.name })),
  [states]);
  const cityOpts = useMemo(() =>
    cities.map((c) => ({ value: c.name, label: c.name })),
  [cities]);

  const pickCountry = useCallback((iso) => {
    const c = countries.find((x) => x.isoCode === iso);
    // Clear state/city whenever country changes — they belong to the old country.
    onChange({ hq_country: c?.name || '', hq_state: '', hq_city: '' });
  }, [countries, onChange]);

  const pickState = useCallback((iso) => {
    const s = states.find((x) => x.isoCode === iso);
    onChange({ hq_state: s?.name || '', hq_city: '' });
  }, [states, onChange]);

  const pickCity = useCallback((name) => onChange({ hq_city: name || '' }), [onChange]);

  const star = required ? <span className="text-red-500 ml-0.5">*</span> : null;
  // State + City are optional in the Discover admin flow (nation-wide
  // opportunities are common). Skip the required-star on those fields
  // whenever `optionalStateCity` is on.
  const regionStar = required && !optionalStateCity ? <span className="text-red-500 ml-0.5">*</span> : null;
  const statePlaceholder = country
    ? (optionalStateCity ? 'Leave blank for all states' : 'Select state…')
    : 'Pick a country first';
  const cityPlaceholder = state
    ? (optionalStateCity ? 'Leave blank for all cities' : 'Select city…')
    : (optionalStateCity && country ? 'Leave blank for all cities' : 'Pick a state first');
  // In optional mode we let the admin skip straight from Country → City
  // (e.g. "Bengaluru" without picking Karnataka first).
  const cityDisabled = optionalStateCity ? !country : !state;

  return (
    <div>
      <Label className="flex items-center gap-1.5 text-xs font-semibold">
        <MapPin className="h-3.5 w-3.5 text-primary" /> Location{required && <span className="text-red-500">*</span>}
      </Label>
      <div className="grid sm:grid-cols-3 gap-3 mt-2">
        <div>
          <Label className="text-[11px] text-muted-foreground">Country{star}</Label>
          <Combobox
            options={countryOpts}
            value={country?.isoCode || ''}
            onChange={pickCountry}
            placeholder="Select country…"
            searchPlaceholder="Search countries…"
            className="mt-1"
            testid={`${testidPrefix}-country`}
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">State / Region{regionStar}</Label>
          <Combobox
            options={stateOpts}
            value={state?.isoCode || ''}
            onChange={pickState}
            placeholder={statePlaceholder}
            searchPlaceholder="Search states…"
            emptyText={country ? 'No states listed — leave blank.' : 'Pick a country first.'}
            disabled={!country}
            className="mt-1"
            testid={`${testidPrefix}-state`}
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">City{regionStar}</Label>
          <Combobox
            options={cityOpts}
            value={value.hq_city || ''}
            onChange={pickCity}
            placeholder={cityPlaceholder}
            searchPlaceholder="Search cities…"
            emptyText={state ? 'No cities listed — leave blank.' : (optionalStateCity && country ? 'No cities listed for this country.' : 'Pick a state first.')}
            disabled={cityDisabled}
            className="mt-1"
            testid={`${testidPrefix}-city`}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
